import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Avatar,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  Search as SearchIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import { ChatInterface } from './ChatInterface';
import { ConversationHistory, Conversation } from './ConversationHistory';
import { FilterPanel } from './FilterPanel';
import { AdvancedSearch } from './AdvancedSearch';
import { useDocumentFilters } from '../hooks/useDocumentFilters';
import { useConversationManagement } from '../hooks/useConversationManagement';
import { useAuth } from '../contexts/AuthContext';
import { ChatMessage, ChatResponse, DocumentFilters } from '@nvgc/shared';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiClient';
import { ChatRequest } from '@nvgc/shared';

const DRAWER_WIDTH = 320;

export const ChatPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [userPlan] = useState<'trial' | 'pro' | 'team' | 'enterprise'>('trial');
  
  // Document filters hook
  const {
    filters,
    setFilters
  } = useDocumentFilters({
    syncWithUrl: true,
    defaultFilters: {
      categories: [],
      documentSource: 'all'
    }
  });

  const conversationManagement = useConversationManagement();

  // Load conversations and tags on mount
  useEffect(() => {
    loadConversations();
    loadAvailableTags();
  }, []);

  const loadAvailableTags = async () => {
    try {
      const tags = await conversationManagement.getAvailableTags();
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to load available tags:', error);
    }
  };

  // Load messages when conversation changes
  useEffect(() => {
    if (currentConversation) {
      loadConversationMessages(currentConversation);
    } else {
      setMessages([]);
    }
  }, [currentConversation]);

  const loadConversations = async () => {
    try {
      const { data } = await api.get<{
        conversations: Array<{
          id: string;
          title?: string;
          created_at: string;
          updated_at: string;
        }>;
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>('/api/conversations?page=1&pageSize=20');

      const mappedConversations: Conversation[] = data.conversations.map(conv => ({
        id: conv.id,
        title: conv.title || 'New Conversation',
        createdAt: new Date(conv.created_at),
        updatedAt: new Date(conv.updated_at),
        messageCount: 0, // Will be updated when loading messages
        lastMessage: '',
        tags: [],
        isFavorite: false
      }));

      setConversations(mappedConversations);
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
      // Don't show error for initial load, just use empty array
      setConversations([]);
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    try {
      setIsLoading(true);
      
      const { data } = await api.get<{
        id: string;
        user_id: string;
        created_at: string;
        updated_at: string;
        chat_messages: Array<{
          id: string;
          conversation_id: string;
          role: 'user' | 'assistant';
          content: string;
          citations?: string;
          tokens_used?: number;
          created_at: string;
        }>;
      }>(`/conversations/${conversationId}`);

      if (!data) {
        setError('Conversation not found');
        return;
      }

      const mappedMessages: ChatMessage[] = data.chat_messages.map(msg => ({
        id: msg.id,
        conversationId: msg.conversation_id,
        role: msg.role,
        content: msg.content,
        citations: msg.citations ? JSON.parse(msg.citations) : [],
        tokensUsed: msg.tokens_used,
        createdAt: new Date(msg.created_at)
      }));

      setMessages(mappedMessages);
    } catch (err: any) {
      console.error('Failed to load messages:', err);
      setError('Failed to load conversation messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    setError(undefined);
    setIsLoading(true);

    // Create user message
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      conversationId: currentConversation || 'new',
      role: 'user',
      content: message,
      createdAt: new Date(),
      citations: []
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);

    try {
      // Prepare chat request
      const request: ChatRequest = {
        message,
        conversationId: currentConversation || undefined,
        filters,
        searchScope: filters.documentSource
      };

      // Send request to backend API
      const { data: chatResponse } = await api.post<ChatResponse>('/api/chat', request);

      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        conversationId: chatResponse.conversationId,
        role: 'assistant',
        content: chatResponse.response,
        createdAt: new Date(),
        citations: chatResponse.citations,
        tokensUsed: chatResponse.tokensUsed
      };

      setMessages(prev => [...prev, assistantMessage]);

      // If this is a new conversation, set it as current
      if (!currentConversation) {
        setCurrentConversation(chatResponse.conversationId);
        // Reload conversations to include the new one
        loadConversations();
      }

    } catch (err: any) {
      console.error('Failed to send message:', err);
      
      // Remove the user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      
      // Extract user-friendly error message
      if (err.userMessage) {
        setError(err.userMessage);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    setCurrentConversation(conversationId);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleDeleteConversation = async (conversationId: string, permanent = false) => {
    try {
      await conversationManagement.deleteConversation({ conversationId, permanent });
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (currentConversation === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  const handleRenameConversation = async (conversationId: string, newTitle: string) => {
    try {
      await conversationManagement.updateConversation(conversationId, { title: newTitle });
      setConversations(prev => 
        prev.map(c => c.id === conversationId ? { ...c, title: newTitle } : c)
      );
    } catch (err) {
      console.error('Failed to rename conversation:', err);
      setError('Failed to rename conversation');
    }
  };

  const handleSearchConversations = useCallback(async (query?: string, tags?: string[], favoritesOnly = false) => {
    try {
      const result = await conversationManagement.searchConversations({
        query,
        tags,
        favoritesOnly,
        page: 1,
        pageSize: 50
      });
      setConversations(result.conversations);
    } catch (err) {
      console.error('Failed to search conversations:', err);
      setError('Failed to search conversations');
    }
  }, [conversationManagement]);

  const handleUpdateConversation = async (conversationId: string, updates: { title?: string; tags?: string[]; isFavorite?: boolean }) => {
    try {
      await conversationManagement.updateConversation(conversationId, updates);
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, ...updates } : c
      ));
      // Reload tags if tags were updated
      if (updates.tags) {
        loadAvailableTags();
      }
    } catch (err) {
      console.error('Failed to update conversation:', err);
      setError('Failed to update conversation');
    }
  };

  const handleExportConversation = async (conversationId: string, format: 'json' | 'pdf' | 'txt' | 'csv') => {
    try {
      await conversationManagement.exportConversation({
        conversationId,
        format,
        includeMetadata: true,
        includeCitations: true
      });
    } catch (err) {
      console.error('Failed to export conversation:', err);
      setError('Failed to export conversation');
    }
  };

  const handleShareConversation = async (conversationId: string, email: string, permission: 'read' | 'comment' | 'edit') => {
    try {
      await conversationManagement.shareConversation({
        conversationId,
        shareWithEmail: email,
        permissionLevel: permission
      });
    } catch (err) {
      console.error('Failed to share conversation:', err);
      setError('Failed to share conversation');
    }
  };

  const handleNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleFiltersChange = (newFilters: DocumentFilters) => {
    setFilters(newFilters);
    // Optionally collapse filters after applying
    if (isMobile) {
      setFiltersCollapsed(true);
    }
  };

  const handleAdvancedSearch = (query: string, searchFilters: DocumentFilters) => {
    // Update filters if they've changed
    if (JSON.stringify(searchFilters) !== JSON.stringify(filters)) {
      setFilters(searchFilters);
    }
    
    // Send the advanced search query
    handleSendMessage(query);
    
    // Hide advanced search after use
    setShowAdvancedSearch(false);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (err) {
      console.error('Failed to sign out:', err);
    }
    handleUserMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleUserMenuClose();
  };

  const handleDocuments = () => {
    navigate('/documents');
    handleUserMenuClose();
  };

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* User Profile Section */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2,
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={handleUserMenuOpen}
        >
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
            <AccountIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {user?.email || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click for options
            </Typography>
          </Box>
        </Box>
        
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewConversation}
          sx={{ mb: 1 }}
        >
          New Conversation
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
          sx={{ mb: 2 }}
        >
          Advanced Search
        </Button>
      </Box>
      <Box sx={{ flex: 1 }}>
        <ConversationHistory
          conversations={conversations}
          selectedConversationId={currentConversation || undefined}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onRenameConversation={handleRenameConversation}
          onSearchConversations={handleSearchConversations}
          onUpdateConversation={handleUpdateConversation}
          onExportConversation={handleExportConversation}
          onShareConversation={handleShareConversation}
          availableTags={availableTags}
          userPlan={userPlan}
          isLoading={conversationManagement.isLoading}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex' }}>
      {/* Mobile Menu Button */}
      {isMobile && (
        <Box sx={{ position: 'fixed', top: 16, left: 16, zIndex: 1300 }}>
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Paper sx={{ width: DRAWER_WIDTH, flexShrink: 0 }} variant="outlined">
          {drawerContent}
        </Paper>
      )}

      {/* Main Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          isCollapsed={filtersCollapsed}
          onToggleCollapse={() => setFiltersCollapsed(!filtersCollapsed)}
        />

        {/* Advanced Search Panel */}
        {showAdvancedSearch && (
          <Box sx={{ mb: 2 }}>
            <AdvancedSearch
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onSearch={handleAdvancedSearch}
            />
          </Box>
        )}

        {!currentConversation && messages.length === 0 && !showAdvancedSearch ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: 4
            }}
          >
            <Typography variant="h4" gutterBottom color="text.secondary">
              Nevada Gaming Compliance AI Assistant
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
              Ask questions about Nevada gaming regulations, statutes, and technical standards.
              Get accurate answers with proper citations from official sources.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use the filters above to narrow your search to specific document types, categories, or date ranges.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleNewConversation}
            >
              Start New Conversation
            </Button>
          </Box>
        ) : (
          <ChatInterface
            onSendMessage={handleSendMessage}
            messages={messages}
            isLoading={isLoading}
            error={error}
            onRetry={() => {
              // Clear error and retry last message if available
              setError(undefined);
              const lastUserMessage = messages.filter(m => m.role === 'user').pop();
              if (lastUserMessage) {
                handleSendMessage(lastUserMessage.content);
              }
            }}
          />
        )}
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleProfile}>
          <PersonIcon sx={{ mr: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleDocuments}>
          <DocumentIcon sx={{ mr: 1 }} />
          Documents
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Sign Out
        </MenuItem>
      </Menu>
    </Box>
  );
};